import { useMemo, useState, useEffect } from "react";
import { LOCAL_STORAGE_KEYS, USER_ROLE_CODE } from "../../constants/Constants";
import storage from "../../services/storage";

/**
 * Returns contact feature flags based on the current user's role and domain configuration.
 *
 * Access matrix:
 *  - SA / ADM / UM  : Full access (add, edit, delete, import, export, favorites)
 *  - INT            : View + add + edit + favorites (no delete, import, export)
 *  - DEV / BL / DA  : View only
 *  - Others (EXT …) : View only
 *
 * `isContactsEnabled` reflects the `contactmanagement` domain config key.
 * When false the contacts feature should be hidden entirely.
 *
 * Re-reads localStorage whenever api.js fires 'domainConfigUpdated' so that
 * admin configuration changes are reflected without a full page reload.
 */
const useContactsPermissions = () => {
  // Incrementing this counter whenever the domain config is refreshed causes
  // useMemo to re-run and pick up the latest localStorage value.
  const [configTick, setConfigTick] = useState(0);

  useEffect(() => {
    const handler = () => setConfigTick((t) => t + 1);
    window.addEventListener("domainConfigUpdated", handler);
    return () => window.removeEventListener("domainConfigUpdated", handler);
  }, []);

  return useMemo(() => {
    const userData = storage.get(LOCAL_STORAGE_KEYS.USER_DATA);
    const domainConfig = storage.get(LOCAL_STORAGE_KEYS.DOMAIN_CONFIGURATION);

    const roleCode = userData?.roleCode;
    const isContactsEnabled = Number(domainConfig?.configurations?.contactmanagement) === 1;

    const isFullAccess = [
      USER_ROLE_CODE.superAdmin,
      USER_ROLE_CODE.administrator,
      USER_ROLE_CODE.userManager,
    ].includes(roleCode);

    const isInternal = roleCode === USER_ROLE_CODE.internal;

    return {
      isContactsEnabled,
      canAdd: isFullAccess || isInternal,
      canEdit: isFullAccess || isInternal,
      canDelete: isFullAccess,
      canImport: isFullAccess,
      canExport: isFullAccess,
      canFavorite: true,
      /** ContactPicker in invite modal: contacts must be enabled + role can at least view */
      showContactPicker: isContactsEnabled,
    };
  }, [configTick]); // eslint-disable-line react-hooks/exhaustive-deps
};

export default useContactsPermissions;
